'use client'

/*
 * fn: createDynamicComponent
 *
 * Creates a dynamically lazy-loaded Component with preset data and an error boundary
 *
 */
import dynamic, { type DynamicOptions, type DynamicOptionsLoadingProps } from 'next/dynamic'
import { type ComponentProps, type ComponentType, type ReactNode } from 'react'

import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { LoadingMask } from '@/components/common/LoadingMask'

interface Props<K extends string, M extends Record<K, ComponentType<any>>> {
  fallback?: ReactNode
  loader: () => Promise<M>
  loading?: NonNullable<DynamicOptions['loading']> | false
  name: K
}

const LOADING_DEFAULT = ({ pastDelay }: DynamicOptionsLoadingProps) =>
  pastDelay ? <LoadingMask bgNone blurNone loading showShimmer={false} showSpinner /> : null

const LOADING_NONE = () => null

export const createDynamicComponent = <K extends string, M extends Record<K, ComponentType<any>>>({
  fallback,
  loader,
  loading = LOADING_DEFAULT,
  name,
}: Props<K, M>) => {
  type ComponentPropsType = ComponentProps<M[K]>

  const Loading = loading || LOADING_NONE

  const LoadingState = (props: DynamicOptionsLoadingProps) => {
    if (props.error) throw props.error
    return <Loading {...props} />
  }

  const Dynamic = dynamic<ComponentPropsType>(async () => (await loader())[name], {
    loading: LoadingState,
    ssr: false,
  })

  const Component = (props: ComponentPropsType) => (
    <ErrorBoundary fallback={fallback}>
      <Dynamic {...props} />
    </ErrorBoundary>
  )
  Component.displayName = name

  return Component
}
